// May 28, 2026 v2 — Per-silo LLM provider trackers (in-silo OpsTab).
//
// Mirrors the dashboard ProviderTrackersCard split-row structure exactly:
//   • RED row (platform vouchers) — one pill per provider where the user
//     has NO BYOK key. Pill big number = THIS silo's slice of that bucket.
//   • GREEN row (BYOK self-balance) — one pill per provider where user
//     HAS a BYOK key. Pill big number = THIS silo's slice of the BYOK
//     self-balance.
//
// Same Redistribute popup is reused (loads all silos, focuses this silo
// for offset default). No Flush button at silo level — flush is a
// portfolio-wide action that lives only on the Dashboard.
import { useEffect, useState } from "react";
import { Wallet, Info, Minus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import VoucherTopupModal from "@/components/VoucherTopupModal";
import VoucherHeldStrip from "@/components/VoucherHeldStrip";
import VoucherRestrictionNotice from "@/components/VoucherRestrictionNotice";
import VoucherActivityFeed from "@/components/VoucherActivityFeed";
import { ProviderTrackerTile, RedistributePopup } from "@/components/ProviderTrackerCore";

const PROV_IDS = ["gemini", "anthropic", "openai"];

export default function SiloVoucherPanel({ siloId }) {
  const [data, setData] = useState(null);
  const [byok, setByok] = useState({});
  const [totals, setTotals] = useState({ platform: {}, byok: {} });
  const [loading, setLoading] = useState(true);
  const [topupProvider, setTopupProvider] = useState(null);
  const [redistProvider, setRedistProvider] = useState(null);
  const [redistByokMode, setRedistByokMode] = useState(false);
  const [revealHiddenRed, setRevealHiddenRed] = useState(false);
  const [frozenCents, setFrozenCents] = useState(0);
  const [restrictions, setRestrictions] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [silo, summary] = await Promise.all([
        api.get(`/silos/${siloId}/voucher`),
        api.get(`/me/voucher-summary`),
      ]);
      setData(silo.data);
      setByok(summary.data?.byok_status || {});
      // Cross-silo totals (for the per-pill "[$total ALL]" peek button).
      setTotals({
        platform: summary.data?.provider_totals_cents || {},
        byok: summary.data?.byok_self_balance_cents || {},
      });
      // Jun 2026 (Pass 3) — this silo's slice of credit frozen by an open
      // dispute, so the tiles never appear to shrink for no reason.
      const row = (summary.data?.silos || []).find((s) => s.silo_id === siloId);
      setFrozenCents(Number(row?.frozen_voucher_cents || 0));
      setRestrictions(summary.data?.restrictions || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load silo trackers");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (siloId) load(); }, [siloId]);

  if (loading || !data) {
    return (
      <div className="bg-white border hairline rounded-lg p-3" data-testid="silo-voucher-loading">
        <div className="text-sm text-[#6B6A68]">Loading silo trackers…</div>
      </div>
    );
  }

  const platformBuckets = {
    gemini: Number(data.gemini_voucher_cents || 0),
    anthropic: Number(data.anthropic_voucher_cents || 0),
    openai: Number(data.openai_voucher_cents || 0),
  };
  const byokBalances = {
    gemini: Number(data.byok_self_balance_gemini_cents || 0),
    anthropic: Number(data.byok_self_balance_anthropic_cents || 0),
    openai: Number(data.byok_self_balance_openai_cents || 0),
  };
  const spent30 = data.provider_spent_30d_cents || {};

  const redProviders = PROV_IDS.filter((p) => !byok[p]);
  const greenProviders = PROV_IDS.filter((p) => !!byok[p]);
  const redProvidersDisplay = revealHiddenRed ? PROV_IDS : redProviders;
  const hasHiddenRedFunds = greenProviders.some((p) => (platformBuckets[p] || 0) > 0);

  const openRedistribute = (prov, byokModeFlag) => {
    setRedistByokMode(!!byokModeFlag);
    setRedistProvider(prov);
  };

  return (
    <>
      <VoucherRestrictionNotice restrictions={restrictions} context="silo" />
      <VoucherHeldStrip
        availableCents={PROV_IDS.reduce((a, p) => a + (platformBuckets[p] || 0), 0)}
        heldCents={frozenCents}
        context="silo"
      />
      {redProvidersDisplay.length > 0 && (
        <div className="bg-white border hairline rounded-lg p-3 mb-3" data-testid="silo-trackers-red-row">
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={14} weight="duotone" className="text-[#C16E5A] shrink-0" />
            <div className="text-xs font-medium text-[#1C1B1A]">
              Platform vouchers · this silo's slice ({redProvidersDisplay.length})
              {revealHiddenRed && (
                <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-[#A89360]">· revealed</span>
              )}
            </div>
            <span
              className="inline-flex items-center text-[#6B6A68] hover:text-[#1C1B1A] cursor-help"
              data-testid="silo-trackers-red-info"
              title="This silo's portion of each platform voucher you've paid into. Decrements in real time as platform-mode calls land. Add a BYOK key for a provider to switch off 3× markup (current balance refunds to your Stripe Express)."
            >
              <Info size={12} weight="duotone" />
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {redProvidersDisplay.map((p) => (
              <ProviderTrackerTile
                key={p}
                provider={p}
                byok={false}
                bucketCents={platformBuckets[p]}
                spent30Cents={spent30[p] || 0}
                peek={{ context: "silo", totalCents: totals.platform[p] || 0, focusSiloId: siloId }}
                onRedistributeClick={(prov) => openRedistribute(prov, false)}
                onTopupClick={(prov) => setTopupProvider(prov)}
              />
            ))}
          </div>
        </div>
      )}

      {greenProviders.length > 0 && (
        <div className="bg-white border hairline rounded-lg p-3" data-testid="silo-trackers-green-row">
          <div className="flex items-center justify-end mb-1 -mt-1">
            <button
              type="button"
              onClick={() => setRevealHiddenRed((v) => !v)}
              data-testid="silo-trackers-reveal-red-toggle"
              title={revealHiddenRed ? "Hide platform voucher tiles" : "Show hidden platform voucher tiles for this silo"}
              className={`inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded border transition-colors ${revealHiddenRed ? "border-[#C16E5A] text-[#C16E5A] bg-[#FDF1ED]" : "border-[#D8D6D2] text-[#6B6A68] hover:border-[#A89360] hover:text-[#1C1B1A]"}`}
            >
              <Minus size={9} weight="bold" />
              {revealHiddenRed ? "Hide platform tiles" : "Show platform tiles"}
              {!revealHiddenRed && hasHiddenRedFunds && (
                <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-[#C16E5A]" title="Stale platform funds in this silo" />
              )}
            </button>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={14} weight="duotone" className="text-[#4A5D4E] shrink-0" />
            <div className="text-xs font-medium text-[#1C1B1A]">
              Your provider balances · this silo's slice ({greenProviders.length})
            </div>
            <span
              className="inline-flex items-center text-[#6B6A68] hover:text-[#1C1B1A] cursor-help"
              data-testid="silo-trackers-green-info"
              title="This silo's slice of each BYOK self-balance. Decrements at raw cost on every BYOK call. Click Redistribute to re-sync the cross-silo total to your real balance."
            >
              <Info size={12} weight="duotone" />
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {greenProviders.map((p) => (
              <ProviderTrackerTile
                key={p}
                provider={p}
                byok={true}
                bucketCents={byokBalances[p]}
                spent30Cents={spent30[p] || 0}
                peek={{ context: "silo", totalCents: totals.byok[p] || 0, focusSiloId: siloId }}
                onRedistributeClick={(prov) => openRedistribute(prov, true)}
                onTopupClick={(prov) => setTopupProvider(prov)}
              />
            ))}
          </div>
        </div>
      )}

      <VoucherActivityFeed limit={8} />

      <VoucherTopupModal
        open={!!topupProvider}
        onClose={() => setTopupProvider(null)}
        onSuccess={load}
        prefocusedSiloId={siloId}
        defaultProvider={topupProvider}
      />
      <RedistributePopup
        open={!!redistProvider}
        onClose={() => setRedistProvider(null)}
        provider={redistProvider}
        focusSiloId={siloId}
        byokMode={redistByokMode}
        onSaved={load}
      />
    </>
  );
}
